import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useOnClickOutside } from 'usehooks-ts'

export default ({
  selectedOption,
  options,
  onChange,
}: {
  selectedOption: string
  options: string[]
  onChange: (option: string) => void
}) => {
  const [showOptions, setShowOptions] = useState(false)
  const optionsRef = useRef<HTMLUListElement>(null)
  // add event listener to close options when clicking outside of the select
  useOnClickOutside(optionsRef, (e) => {
    setShowOptions(false)
  })
  // handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // only handle if options are open
      if (!showOptions) return
      if (e.key === 'Escape') {
        setShowOptions(false)
      }
      // handle enter
      if (e.key === 'Enter') {
        // get focused option
        const focusedOption = optionsRef.current?.querySelector(
          'li:focus'
        )?.getAttribute('data-value')
        if(focusedOption){
          onChange(focusedOption)
          setShowOptions(false)
        }
      }
      // handle any letter key
      if (e.key.match(/^[a-zA-Z]$/)) {
        // find the next option that starts with the pressed key
        const nextOption = options.find((option) =>
          option.toLowerCase().startsWith(e.key.toLowerCase())
        )
        // set it as focus
        if (nextOption) {
          const nextOptionElement = optionsRef.current?.querySelector(
            `li[data-value="${nextOption}"]`
          )
          if (nextOptionElement instanceof HTMLElement) {
            nextOptionElement.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  })
  return (
    <CustomSelectWrapper aria-expanded={showOptions}>
      <SelectedOption
        isOpen={showOptions}
        onMouseDown={(e) => {
          e.stopPropagation()
          setShowOptions(!showOptions)
        }}
      >
        {selectedOption}{' '}
        <span className='icon'>
          <svg
            width='1em'
            height='1em'
            viewBox='0 0 12 12'
            xmlns='http://www.w3.org/2000/svg'
          >
            <polyline
              fill='none'
              strokeWidth='1.5'
              points='2 4 6.2560339 8.2560339 10.5120678 4'
            ></polyline>
          </svg>
        </span>
      </SelectedOption>

      <Options
        showOptions={showOptions}
        aria-roledescription='listbox'
        ref={optionsRef}
      >
        {options.map((option) => {
          return (
            <Option
              key={option}
              onMouseDown={() => {
                setShowOptions(false)
                onChange(option)
              }}
              isSelected={option === selectedOption}
              tabIndex={0}
              aria-roledescription='option'
              data-value={option}
            >
              {option}
            </Option>
          )
        })}
      </Options>
    </CustomSelectWrapper>
  )
}
const CustomSelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  text-align: left;
  width: min-content;
  white-space: nowrap;
`
const SelectedOption = styled.div<{ isOpen?: boolean }>`
  cursor: pointer;
  display: flex;
  font-weight: 600;
  border: 2px solid currentColor;
  padding: 0.25em 0.5em;
  margin: 0.25em 0;
  border-radius: 0.25em;
  user-select: none;
  .icon {
    margin-left: 0.1em;
    padding-top: 2px;
    display: flex;
    align-items: center;
    transform: rotate3d(
      0,
      0,
      1,
      ${(props) => (props.isOpen ? '180deg' : '0deg')}
    );
    transition: transform 0.3s ease;
    svg {
      display: block;
      stroke: currentColor;
    }
  }
`
const Options = styled.ul<{ showOptions: boolean }>`
  //reset ul styles
  list-style: none;
  margin: 0;
  padding: 0;
  // custom styles
  white-space: nowrap;
  position: absolute;
  z-index: 4;
  top: 2.1em;
  left: 50%;
  transform: translateX(-50%);
  transition: max-height 0.3s ease;
  max-height: ${({ showOptions }) => (showOptions ? '500px' : '0')};
  overflow-y: scroll;
  border-radius: 0.5em;
  // smooth shadow
  box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.25);
  // custom scrollbar
  scrollbar-width: thin;
  scrollbar-color: var(--site-background) var(--beige-dark-1);
  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background: var(--site-background);
    border-radius: 0em;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--beige-dark-1);
    border-radius: 20px;
    border: none;
  }
  background-color: var(--site-background);
`
const Option = styled.li<{ isSelected?: boolean }>`
  cursor: pointer;
  pointer-events: all;
  padding: 0.25em 0.5em;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : 'inherit')};
  &:hover {
    /* font-weight: 600; */
    background-color: var(--beige-bright-2);
  }
`
